// Per-field editing for an admin screen that edits one existing record.
//
// The screen still holds one draft object, the way a form always did, but
// each key commits on its own tick: the PATCH carries that field alone, so
// a half-finished thought in one box can never ride along with a deliberate
// change in another. On a "new" screen there is no record to PATCH into, so
// the hook is created with `enabled: false` — every field then behaves like
// an ordinary controlled input and the screen keeps its Create button.
//
// Keys are addressed by name rather than by a hook per field, because admin
// forms show and hide fields as you fill them in (a condition that is a link
// row has no search metadata) and hooks may not come and go.
import * as React from "react";
import { inlinePatch } from "@/lib/inline-save";
import type { InlineEditState } from "@/components/ui/inline-edit";

function sameValue(a: unknown, b: unknown) {
  if (Object.is(a, b)) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

export type InlineRecord<R extends Record<string, unknown>> = {
  /** The whole draft — what a "new" screen submits in one go. */
  values: R;
  value: <K extends keyof R>(key: K) => R[K];
  set: <K extends keyof R>(key: K, next: R[K]) => void;
  /** Change several keys at once without committing any of them (a slug
   *  derived from a title, say). Each still ticks on its own. */
  patch: (changes: Partial<R>) => void;
  /** Everything a field needs: draft value, dirty flag, tick and cross. */
  state: <K extends keyof R>(key: K, options?: FieldOptions<R, K>) => InlineEditState<R[K]>;
  /** True while any field is mid-save. */
  busy: boolean;
  enabled: boolean;
};

export type FieldOptions<R, K extends keyof R> = {
  /** What actually goes on the wire — a number input holds a string, a date
   *  picker an ISO day. Defaults to the draft value itself. */
  serialize?: (value: R[K]) => unknown;
  /** Keys that have to travel with this one because the server reads them
   *  together (a photo id beside its upload). */
  with?: (draft: R) => Record<string, unknown>;
};

export function useInlineRecord<R extends Record<string, unknown>>({
  record,
  url,
  param,
  save: customSave,
  enabled = true,
}: {
  record: R;
  /** Where a single field's PATCH goes — the record's own update path. */
  url?: string;
  /** The strong-parameters key the server wraps attributes in ("condition"). */
  param?: string;
  /** For a row that isn't one field per column: a page section stores all of
   *  its copy in one JSON blob, so committing one box means sending the blob
   *  the server already holds with that single key replaced. */
  save?: (key: string, value: unknown) => Promise<void>;
  enabled?: boolean;
}): InlineRecord<R> {
  const [base, setBase] = React.useState<R>(record);
  const [draft, setDraft] = React.useState<R>(record);
  const [saving, setSaving] = React.useState<Record<string, boolean>>({});
  const [errors, setErrors] = React.useState<Record<string, string | null>>({});
  const [versions, setVersions] = React.useState<Record<string, number>>({});
  // What the server last told us, key by key. Every inline save reloads the
  // page's props, so most keys arrive unchanged and must not disturb an edit
  // in progress; only a key that genuinely moved is adopted.
  const seen = React.useRef<R>(record);

  React.useEffect(() => {
    const moved = Object.keys(record).filter((key) => !sameValue(seen.current[key], record[key]));
    if (moved.length === 0) return;
    const adopted = Object.fromEntries(moved.map((key) => [key, record[key]]));
    seen.current = record;
    setBase((prev) => ({ ...prev, ...adopted }));
    setDraft((prev) => ({ ...prev, ...adopted }));
    setVersions((prev) => {
      const next = { ...prev };
      for (const key of moved) next[key] = (next[key] ?? 0) + 1;
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      for (const key of moved) next[key] = null;
      return next;
    });
  }, [record]);

  function set<K extends keyof R>(key: K, next: R[K]) {
    setDraft((prev) => ({ ...prev, [key]: next }));
  }

  function patch(changes: Partial<R>) {
    setDraft((prev) => ({ ...prev, ...changes }));
  }

  function save<K extends keyof R>(key: K, options?: FieldOptions<R, K>) {
    const name = String(key);
    const next = draft[key];
    if (!enabled || saving[name] || sameValue(next, base[key])) return;

    setSaving((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: null }));

    const wire = options?.serialize ? options.serialize(next) : next;
    const request = customSave
      ? customSave(name, wire)
      : inlinePatch(url as string, {
          [param as string]: { [name]: wire, ...(options?.with ? options.with(draft) : {}) },
        });

    request
      .then(() => {
        seen.current = { ...seen.current, [key]: next };
        setBase((prev) => ({ ...prev, [key]: next }));
      })
      .catch((e: unknown) =>
        setErrors((prev) => ({
          ...prev,
          [name]: e instanceof Error ? e.message : "Could not save.",
        })),
      )
      .finally(() => setSaving((prev) => ({ ...prev, [name]: false })));
  }

  function cancel<K extends keyof R>(key: K) {
    const name = String(key);
    setDraft((prev) => ({ ...prev, [key]: base[key] }));
    setVersions((prev) => ({ ...prev, [name]: (prev[name] ?? 0) + 1 }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  }

  function state<K extends keyof R>(key: K, options?: FieldOptions<R, K>): InlineEditState<R[K]> {
    const name = String(key);
    const commit = () => save(key, options);
    const revert = () => cancel(key);

    return {
      value: draft[key],
      set: (next: R[K]) => set(key, next),
      dirty: enabled && !sameValue(draft[key], base[key]),
      saving: saving[name] ?? false,
      error: errors[name] ?? null,
      save: commit,
      cancel: revert,
      version: versions[name] ?? 0,
      keys: (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          revert();
        }
      },
      multilineKeys: (e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          revert();
        }
      },
    };
  }

  return {
    values: draft,
    value: (key) => draft[key],
    set,
    patch,
    state,
    busy: Object.values(saving).some(Boolean),
    enabled,
  };
}
